import { BpInput } from '../../shared/components/forms/input/BpInput'
import { BpButton } from '../../shared/components/buttons/bp-button/BpButton'
import React, { useEffect, useMemo, useState } from 'react'
import { BpTextArea } from '../../shared/components/forms/textarea/BpTextArea'
import { useTranslation } from 'react-i18next'
import { BpCheckbox } from '../../shared/components/forms/checkbox/BpCheckbox'
import { BpSelect } from '../../shared/components/forms/select/BpSelect'
import { useForm } from 'react-hook-form'
import {
  GetFilterCityApiArg,
  useGetFilterBrowserLanguageQuery,
  useGetFilterCityQuery,
  useGetFilterCountryQuery,
  useGetFilterDeviceTypesQuery,
  useGetFilterEngineQuery,
  useGetProjectLocationsDefaultSettingsQuery,
  usePostProjectCreateMutation,
} from '../../app/api'
import { IOption } from '../../shared/components/forms/search-input/BpSearchInput'
import { parseDomainList } from '../../shared/lib/helpers/ExtractHostname'
import { Tooltip } from '../../shared/components/text/tooltip/Tooltip'
import { IconArrowBack } from '../../shared/components/icons/IconArrowBack'
import { IconLogout } from '../../shared/components/icons/IconLogout'
import { useAuth } from '../../shared/lib/hooks/UseAuth'
import { useNavigate } from 'react-router-dom'
import { selectUser, setTariffUpdated } from '../../app/store/reducers/AuthSlice'
import { useAppDispatch, useAppSelector } from '../../app/store/hooks/Redux'
import { ImageStepFirst } from './ui/ImageStepFirst'
import { ImageStepSecond } from './ui/ImageStepSecond'
import { ImageStepThird } from './ui/ImageStepThird'
import { ImageStepFour } from './ui/ImageStepFour'
import { ImageStepFive } from './ui/ImageStepFive'
import { useAiChat } from '../../shared/components/ai/ai-provider/AiProvider'
import { AiButton } from '../../shared/components/ai/ai-button/AiButton'
import { AiDrawer } from '../../shared/components/ai/ai-drawer/AiDrawer'
import { ALL_CITIES_VALUE } from '../../shared/consts/AllCities.const'
import clsx from 'clsx'

import styles from './OnboardingPage.module.scss'
import { renderCountryOption } from '../../shared/components/utility/country-option/CountryOption'

export const OnboardingPage = () => {
  const { t } = useTranslation()
  const { handleSendMessage } = useAiChat()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [step, setStep] = useState<number>(1)
  const [disabled, setDisabled] = useState(true)
  const { id: userId, company_id: companyId, ...user } = useAppSelector(selectUser)
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!user?.allow_onboarding) {
      navigate('/projects')
    }
  }, [user])

  const [createProject, { isLoading: createProjectLoading }] = usePostProjectCreateMutation()

  const {
    register,
    setValue,
    setError,
    clearErrors,
    getValues,
    watch,
    formState: { errors, isValid },
  } = useForm<any>({
    mode: 'onChange',
    defaultValues: {
      project_name: '',
      brand_name: '',
      brand_variants: '',
      domains: '',
      engines: [false, true, false],
      devices: [true, true],
      country: '',
      cities: [],
      browser_language: '',
      terms: '',
    },
  })

  const formValues = watch()

  const { data: engines } = useGetFilterEngineQuery({})
  const { data: devices } = useGetFilterDeviceTypesQuery()
  const { data: countries } = useGetFilterCountryQuery({})
  const { data: cities } = useGetFilterCityQuery(
    {
      all: true,
      countryId: Number(formValues?.country),
    } as unknown as GetFilterCityApiArg,
    { skip: !formValues?.country },
  )
  const {
    data: languages,
    isLoading: languagesIsLoading,
    isFetching: languagesIsFetching,
  } = useGetFilterBrowserLanguageQuery(
    {
      countryId: Number(formValues?.country),
    },
    { skip: !formValues?.country },
  )
  const {
    data: locationsDefaultSettings,
    isLoading: locationsDefaultSettingsLoading,
    isFetching: locationsDefaultSettingsFetching,
  } = useGetProjectLocationsDefaultSettingsQuery(
    {
      countryIds: [Number(formValues?.country)],
    },
    { skip: !formValues?.country },
  )

  const filteredCountries = useMemo(() => {
    const currentEngines = formValues.engines
      .map((e: any, i: any) => (e ? engines?.[i] : undefined))
      .filter(Boolean)

    if (currentEngines.length > 1) return countries

    const engine = currentEngines[0]

    return engine?.allowed_country_ids?.length
      ? countries?.filter((c) => engine.allowed_country_ids?.includes(Number(c.value)))
      : countries
  }, [countries, engines, formValues])

  const domainsValidate = (list: string): string[] => {
    const domainRegex = /^(?:https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/

    return list
      .split('\n')
      .map((line: string) => line.trim())
      .filter((line: string) => line && !domainRegex.test(line))
  }

  const handleSubmit = () => {
    createProject({
      body: {
        step: (
          {
            1: 'onboarding_first',
            2: 'onboarding_second',
            3: 'onboarding_third',
            4: 'onboarding_fourth',
            5: 'onboarding_fifth',
          } as const
        )[step as 1 | 2 | 3 | 4 | 5],
        project_name: step > 0 ? formValues.project_name : undefined,
        brand_name: step > 0 ? formValues.brand_name : undefined,
        brand_variants: step > 1 ? formValues?.brand_variants?.split('\n') : undefined,
        domains: step > 2 ? parseDomainList(formValues?.domains ?? '') : undefined,
        engines:
          step > 2
            ? engines
                ?.filter((_, index) => formValues?.engines[index])
                .map((engine) => ({ id: Number(engine.value) }))
            : undefined,
        devices:
          step > 2
            ? devices
                ?.filter((_, index) => formValues?.devices[index])
                .map((device) => ({ id: Number(device.value) }))
            : undefined,
        locations:
          step > 3
            ? [
                {
                  country_id: Number(formValues?.country),
                  languages: [{ id: formValues?.browser_language }],
                  cities: formValues?.cities?.map((city: string) => ({ id: Number(city) })),
                  engines: engines
                    ?.filter((_, index) => formValues?.engines[index])
                    .map((engine) => ({ id: Number(engine.value) })),
                },
              ]
            : undefined,
        terms:
          step > 4
            ? {
                country_id: Number(formValues?.country),
                terms: formValues?.terms?.split('\n'),
                language_code: formValues?.browser_language,
              }
            : undefined,
      },
    }).then((response: any) => {
      if (step < 5) {
        if (response.data.success) {
          setStep(step + 1)
        }
      } else {
        if (response.data.success) {
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: 'project_created',
            properties: {
              user_id: userId,
              project_id: response.data.data.id,
              project_name: formValues.project_name,
              company_id: companyId,
            },
          })

          dispatch(setTariffUpdated())
          navigate('/projects')
        }
      }
    })
  }

  useEffect(() => {
    const first = !formValues.project_name.length || !formValues.brand_name
    const second = !formValues?.brand_variants.length
    const third =
      !formValues?.domains.length ||
      !!domainsValidate(formValues?.domains).length ||
      !formValues?.engines.filter((engine: boolean) => engine).length ||
      !formValues?.devices.filter((device: boolean) => device).length
    const fourth =
      !formValues?.country?.length ||
      !formValues?.cities?.length ||
      !formValues?.browser_language?.length
    const fifth =
      !formValues.terms.length ||
      formValues.terms
        .replace(',', '\n')
        .split('\n')
        .filter((value: string) => value.length).length > 20

    setDisabled(
      {
        1: first,
        2: second,
        3: third,
        4: fourth,
        5: fifth,
      }[step] || false,
    )
  }, [formValues])

  useEffect(() => {
    setValue('browser_language', '')
  }, [formValues.country])

  useEffect(() => {
    const defaultLanguage = locationsDefaultSettings?.data?.items?.[0]?.languages?.[0]?.id

    if (!defaultLanguage) return

    const timer = setTimeout(() => {
      if (getValues('browser_language')) return

      setValue('browser_language', defaultLanguage)
    }, 100)

    return () => clearTimeout(timer)
  }, [locationsDefaultSettings])

  useEffect(() => {
    setValue('terms', '')
    clearErrors('terms')
  }, [formValues.country, formValues.browser_language])

  useEffect(() => {
    formValues.engines.forEach((selected: any, i: any) => {
      const engine = selected ? engines?.[i] : undefined
      const allowed = engine?.allowed_country_ids

      if (allowed?.length && !allowed.includes(Number(formValues.country))) {
        setValue(
          'engines',
          formValues.engines.map((v: any, j: any) => (i === j ? false : v)),
        )
      }
    })
  }, [formValues.country, engines, formValues.engines, setValue])

  useEffect(() => {
    setValue('cities', cities?.length ? [ALL_CITIES_VALUE] : [])
  }, [cities])

  return (
    <div className={clsx(styles.root)}>
      <div className={clsx(styles.header)}>
        {step > 1 && (
          <BpButton
            iconLeft={<IconArrowBack />}
            size='sm'
            theme='outline'
            onClick={() => setStep(step - 1)}
          >
            {t('buttons.back')}
          </BpButton>
        )}
        <BpButton
          className={clsx(styles.logoutBtn)}
          iconLeft={<IconLogout />}
          size='sm'
          theme='outline'
          onClick={() => logout()}
        >
          {t('buttons.logout')}
        </BpButton>
      </div>
      <div className={clsx(styles.content)}>
        {
          {
            1: (
              <section className={clsx(styles.stepSection)}>
                <div className={clsx(styles.stepContent)}>
                  <p className={clsx(styles.stepLabel)}>
                    {t('pages.onboarding.stepOfSteps', { step, steps: 5 })}
                  </p>
                  <h2 className={clsx(styles.stepTitle)}>{t('pages.onboarding.createProject')}</h2>
                  <div className={clsx(styles.formCol)}>
                    <BpInput
                      required
                      error={errors.project_name?.message as string}
                      info={t('pages.onboarding.placeholders.projectName')}
                      label={t('fields.projectName')}
                      value={formValues.project_name}
                      onClear={() => setValue('project_name', '', { shouldValidate: true })}
                      {...register(
                        'project_name',
                        step === 1
                          ? {
                              onBlur: (e) => setValue('project_name', e.target.value.trim()),
                              required: t('fields.validate.required'),
                              maxLength: {
                                value: 100,
                                message: t('pages.onboarding.validate.projectName'),
                              },
                              pattern: /.*\S+.*/,
                            }
                          : undefined,
                      )}
                    />
                    <BpInput
                      required
                      error={errors.brand_name?.message as string}
                      info={t('pages.onboarding.placeholders.brandName')}
                      label={t('fields.brandName')}
                      value={formValues.brand_name}
                      onClear={() => setValue('brand_name', '', { shouldValidate: true })}
                      {...register(
                        'brand_name',
                        step === 1
                          ? {
                              onBlur: (e) => setValue('brand_name', e.target.value.trim()),
                              required: t('fields.validate.required'),
                              maxLength: {
                                value: 100,
                                message: t('pages.onboarding.validate.brandName'),
                              },
                              pattern: /.*\S+.*/,
                            }
                          : undefined,
                      )}
                    />
                    <div className={clsx(styles.actionsRow)}>
                      <Tooltip
                        placement={'topLeft'}
                        title={disabled && t('pages.onboarding.completeAllFields')}
                      >
                        <div className={clsx(styles.wFit)}>
                          <BpButton
                            className={clsx(styles.nextBtn)}
                            contentClassName={styles.nextBtnContent}
                            disabled={disabled}
                            size={'lg'}
                            type={'button'}
                            onClick={handleSubmit}
                          >
                            {t('buttons.next')}
                          </BpButton>
                        </div>
                      </Tooltip>
                      <BpButton
                        contentClassName={styles.skipBtnContent}
                        size={'lg'}
                        theme={'transparent'}
                        onClick={() => navigate('/projects')}
                      >
                        {t('buttons.skipForNow')}
                      </BpButton>
                    </div>
                  </div>
                </div>
                <div className={clsx(styles.imageWrapper)}>
                  <ImageStepFirst />
                </div>
              </section>
            ),
            2: (
              <section className={clsx(styles.stepSection)}>
                <div className={clsx(styles.stepContent)}>
                  <p className={clsx(styles.stepLabel)}>
                    {t('pages.onboarding.stepOfSteps', { step, steps: 5 })}
                  </p>
                  <h2 className={clsx(styles.stepTitlePlain)}>
                    {t('pages.onboarding.addMisspellings')}
                  </h2>
                  <p className={clsx(styles.stepSubtitle)}>
                    {t('pages.onboarding.detectInstances')}
                  </p>
                  <div className={clsx(styles.formCol)}>
                    <BpTextArea
                      required
                      rightSection={
                        <AiButton
                          size={'sm'}
                          variant={'outlined'}
                          onClick={() =>
                            handleSendMessage({
                              message: `Generate typos for my brand ${formValues.brand_name}`,
                            })
                          }
                        >
                          {t('buttons.createWithAI')}
                        </AiButton>
                      }
                      info={t('pages.onboarding.placeholders.brandsMisspellingsVariations')}
                      label={t('fields.brandsMisspellingsVariations')}
                      value={formValues.brand_variants}
                      {...register(
                        'brand_variants',
                        step === 2
                          ? {
                              required: t('fields.validate.required'),
                              pattern: /.*\S+.*/,
                              onChange: (e) => {
                                setValue('brand_variants', e.target.value.replace(',', '\n'))
                              },
                              onBlur: (e) => {
                                setValue(
                                  'brand_variants',
                                  e.target.value
                                    .split('\n')
                                    .map((value: string) => value.trim())
                                    .join('\n'),
                                )
                              },
                            }
                          : undefined,
                      )}
                    />
                    <div className={clsx(styles.actionsRow)}>
                      <Tooltip
                        placement={'topLeft'}
                        title={disabled && t('pages.onboarding.completeAllFields')}
                      >
                        <div className={clsx(styles.wFit)}>
                          <BpButton
                            className={clsx(styles.nextBtn)}
                            contentClassName={styles.nextBtnContent}
                            disabled={disabled}
                            size={'lg'}
                            type={'button'}
                            onClick={handleSubmit}
                          >
                            {t('buttons.next')}
                          </BpButton>
                        </div>
                      </Tooltip>
                      <BpButton
                        contentClassName={styles.skipBtnContent}
                        size={'lg'}
                        theme={'transparent'}
                        onClick={() => navigate('/projects')}
                      >
                        {t('buttons.skipForNow')}
                      </BpButton>
                    </div>
                  </div>
                </div>
                <div className={clsx(styles.imageWrapper)}>
                  <ImageStepSecond />
                </div>
              </section>
            ),
            3: (
              <section className={clsx(styles.stepSection)}>
                <div className={clsx(styles.stepContent)}>
                  <p className={clsx(styles.stepLabel)}>
                    {t('pages.onboarding.stepOfSteps', { step, steps: 5 })}
                  </p>
                  <h2 className={clsx(styles.stepTitlePlain)}>
                    {t('pages.onboarding.yourDomains')}
                  </h2>
                  <p className={clsx(styles.stepSubtitle)}>
                    {t('pages.onboarding.listYourBrands')}
                  </p>
                  <div className={clsx(styles.formCol)}>
                    <BpTextArea
                      required
                      error={errors.domains?.message as string}
                      info={t('pages.onboarding.placeholders.domains')}
                      label={t('fields.domains')}
                      value={formValues.domains}
                      {...register(
                        'domains',
                        step === 3
                          ? {
                              required: t('fields.validate.required'),
                              pattern: /.*\S+.*/,
                              onChange: (e) => {
                                setValue(
                                  'domains',
                                  e.target.value
                                    .replace(',', '\n')
                                    .replace(/[^a-zA-Zа-яА-Я0-9\s.,-:/]/g, ''),
                                )
                              },
                              onBlur: (e) => {
                                setValue(
                                  'domains',
                                  e.target.value
                                    .split('\n')
                                    .map((value: string) => value.trim())
                                    .join('\n'),
                                )

                                if (domainsValidate(e.target.value).length > 0) {
                                  setError('domains', { message: t('fields.validate.domain') })
                                }
                              },
                            }
                          : undefined,
                      )}
                    />
                    <div>
                      <div className={clsx(styles.fieldLabel)}>
                        {t('fields.searchEngines')}
                        <span className={styles.requiredMark}> *</span>
                      </div>
                      <div className={clsx(styles.checkboxesRow)}>
                        {engines?.map((engine, index) => {
                          const fieldName = `engines.${index}` as const

                          return (
                            <BpCheckbox
                              key={index}
                              disabled={
                                !!engine.allowed_country_ids?.length &&
                                !!formValues.country &&
                                !engine.allowed_country_ids.includes(Number(formValues.country))
                              }
                              defaultChecked={false}
                              label={engine.text}
                              {...register(fieldName)}
                            />
                          )
                        })}
                      </div>
                    </div>
                    <div>
                      <div className={clsx(styles.fieldLabel)}>
                        {t('fields.deviceTypes')}
                        <span className={styles.requiredMark}> *</span>
                      </div>
                      <div className={clsx(styles.checkboxesRow)}>
                        {devices?.map((device, index) => {
                          const fieldName = `devices.${index}` as const

                          return (
                            <BpCheckbox
                              key={index}
                              defaultChecked={false}
                              label={t('fields.options.' + device.text)}
                              {...register(fieldName)}
                            />
                          )
                        })}
                      </div>
                    </div>
                    <div className={clsx(styles.actionsRow)}>
                      <Tooltip
                        placement={'topLeft'}
                        title={disabled && t('pages.onboarding.completeAllFields')}
                      >
                        <div className={clsx(styles.wFit)}>
                          <BpButton
                            className={clsx(styles.nextBtn)}
                            contentClassName={styles.nextBtnContent}
                            disabled={disabled}
                            size={'lg'}
                            type={'button'}
                            onClick={handleSubmit}
                          >
                            {t('buttons.next')}
                          </BpButton>
                        </div>
                      </Tooltip>
                      <BpButton
                        contentClassName={styles.skipBtnContent}
                        size={'lg'}
                        theme={'transparent'}
                        onClick={() => navigate('/projects')}
                      >
                        {t('buttons.skipForNow')}
                      </BpButton>
                    </div>
                  </div>
                </div>
                <div className={clsx(styles.imageWrapper)}>
                  <ImageStepThird />
                </div>
              </section>
            ),
            4: (
              <section className={clsx(styles.stepSection)}>
                <div className={clsx(styles.stepContent)}>
                  <p className={clsx(styles.stepLabel)}>
                    {t('pages.onboarding.stepOfSteps', { step, steps: 5 })}
                  </p>
                  <h2 className={clsx(styles.stepTitlePlain)}>
                    {t('pages.onboarding.addLocation')}
                  </h2>
                  <p className={clsx(styles.stepSubtitle)}>
                    {t('pages.onboarding.selectOneCountry')}
                  </p>
                  <div className={clsx(styles.formCol)}>
                    <BpSelect
                      required
                      label={t('fields.country')}
                      optionRender={renderCountryOption}
                      options={filteredCountries}
                      placeholder={t('fields.selectCountry')}
                      value={formValues.country}
                      {...register(
                        'country',
                        step === 4
                          ? {
                              required: t('fields.validate.required'),
                              onChange: (e) => {
                                setValue('country', e.target.value)

                                if (!e.target.value) {
                                  setValue('cities', [])
                                  setValue('browser_language', '')
                                }
                              },
                            }
                          : undefined,
                      )}
                    />
                    <BpSelect
                      multiple
                      required
                      disabled={!formValues.country}
                      label={t('fields.cities')}
                      options={cities}
                      placeholder={t('fields.selectCity')}
                      value={formValues.cities}
                      {...register(
                        'cities',
                        step === 4
                          ? {
                              required: t('fields.validate.required'),
                            }
                          : undefined,
                      )}
                      onChange={(e) => {
                        const selected = [].slice
                          .call(e.target.selectedOptions)
                          .map((o: IOption) => o.value)
                        const hasAllCities = selected.includes(ALL_CITIES_VALUE)
                        const hadAllCities = !!formValues.cities?.includes(ALL_CITIES_VALUE)
                        // «All cities» вытесняет остальные города, выбор города — саму опцию
                        const value: string[] =
                          hasAllCities && (!hadAllCities || selected.length === 1)
                            ? [ALL_CITIES_VALUE]
                            : selected.filter((id: string) => id !== ALL_CITIES_VALUE)

                        setValue('cities', value, { shouldValidate: true })
                      }}
                    />
                    <BpSelect
                      required
                      disabled={!formValues.country}
                      label={t('fields.browserLanguage')}
                      loading={languagesIsLoading || languagesIsFetching}
                      options={languages}
                      placeholder={t('fields.selectLanguage')}
                      value={formValues.browser_language}
                      {...register(
                        'browser_language',
                        step === 4
                          ? {
                              required: t('fields.validate.required'),
                            }
                          : undefined,
                      )}
                    />
                    <div className={clsx(styles.actionsRow)}>
                      <Tooltip
                        placement={'topLeft'}
                        title={disabled && t('pages.onboarding.completeAllFields')}
                      >
                        <div className={clsx(styles.wFit)}>
                          <BpButton
                            className={clsx(styles.nextBtn)}
                            contentClassName={styles.nextBtnContent}
                            disabled={disabled}
                            size={'lg'}
                            type={'button'}
                            onClick={handleSubmit}
                          >
                            {t('buttons.next')}
                          </BpButton>
                        </div>
                      </Tooltip>
                      <BpButton
                        contentClassName={styles.skipBtnContent}
                        size={'lg'}
                        theme={'transparent'}
                        onClick={() => navigate('/projects')}
                      >
                        {t('buttons.skipForNow')}
                      </BpButton>
                    </div>
                  </div>
                </div>
                <div className={clsx(styles.imageWrapper)}>
                  <ImageStepFour />
                </div>
              </section>
            ),
            5: (
              <section className={clsx(styles.stepSection)}>
                <div className={clsx(styles.stepContent)}>
                  <p className={clsx(styles.stepLabel)}>
                    {t('pages.onboarding.stepOfSteps', { step, steps: 5 })}
                  </p>
                  <h2 className={clsx(styles.stepTitlePlain)}>
                    {t('pages.onboarding.searchTerms')}
                  </h2>
                  <p className={clsx(styles.stepSubtitle)}>
                    {t('pages.onboarding.limitedSearchTerms')}
                  </p>
                  <div className={clsx(styles.formCol)}>
                    <BpTextArea
                      required
                      label={`${
                        countries?.find((country) => country.value === formValues?.country)?.text
                      }, ${
                        languages?.find(
                          (language) => language.value === formValues?.browser_language,
                        )?.text
                      }${
                        formValues?.terms.length
                          ? `: ${formValues?.terms.split('\n').filter((term: string) => !!term.trim().length).length}`
                          : ''
                      }`}
                      rightSection={
                        <AiButton
                          size={'sm'}
                          variant={'outlined'}
                          onClick={() =>
                            handleSendMessage({
                              message: `Generate keywords for my brand ${
                                formValues.brand_name
                              }, Country ${
                                countries?.find((country) => country.value === formValues?.country)
                                  ?.text
                              }, Language ${
                                languages?.find(
                                  (language) => language.value === formValues?.browser_language,
                                )?.text
                              }`,
                            })
                          }
                        >
                          {t('buttons.createWithAI')}
                        </AiButton>
                      }
                      error={errors.terms?.message as string}
                      height='lg'
                      info={t('pages.onboarding.placeholders.terms')}
                      {...register(
                        'terms',
                        step === 5
                          ? {
                              required: t('fields.validate.required'),
                              onChange: (e) => {
                                const value = e.target.value.replace(',', '\n').split('\n')

                                if (value.find((string: string) => string.length > 50)) {
                                  setValue('terms', formValues.terms)
                                } else {
                                  setValue('terms', value.join('\n'))
                                }
                              },
                              onBlur: (e) => {
                                setValue(
                                  'terms',
                                  e.target.value
                                    .replace(',', '\n')
                                    .split('\n')
                                    .filter((term: string) => !!term.trim().length)
                                    .join('\n'),
                                )

                                if (
                                  e.target.value
                                    .replace(',', '\n')
                                    .split('\n')
                                    .filter((value: string) => value.length).length > 20
                                ) {
                                  setError('terms', {
                                    message: t('pages.onboarding.validate.limitedSearchTerms'),
                                  })
                                }
                              },
                            }
                          : undefined,
                      )}
                    />
                    <div className={clsx(styles.actionsRow)}>
                      <Tooltip
                        placement={'topLeft'}
                        title={disabled && t('pages.onboarding.completeAllFields')}
                      >
                        <div className={clsx(styles.wFit)}>
                          <BpButton
                            className={clsx(styles.nextBtn)}
                            contentClassName={styles.nextBtnContent}
                            disabled={disabled}
                            loading={createProjectLoading}
                            size={'lg'}
                            type={'button'}
                            onClick={handleSubmit}
                          >
                            {t('buttons.create')}
                          </BpButton>
                        </div>
                      </Tooltip>
                      <BpButton
                        contentClassName={styles.skipBtnContent}
                        size={'lg'}
                        theme={'transparent'}
                        onClick={() => navigate('/projects')}
                      >
                        {t('buttons.skipForNow')}
                      </BpButton>
                    </div>
                  </div>
                </div>
                <div className={clsx(styles.imageWrapper)}>
                  <ImageStepFive />
                </div>
              </section>
            ),
          }[step]
        }
      </div>
      <AiDrawer />
    </div>
  )
}
